import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from '../../../../shared/i18n';
import { createSkipRequest } from '../../api/skipRequests';
import { skipActivity } from '../../api/activitySessions';
import { ExternalServiceSession } from './ExternalServiceSession';
import { HandWritingSession } from './HandWritingSession';
import { HandcraftSession } from './HandcraftSession';
import { DrawWithTwoHandSession } from './DrawWithTwoHandSession';
import { WisdomSession } from './WisdomSession';
import { VocabularySession } from './VocabularySession';
import { PhysicalSession } from './PhysicalSession';
import { ActivityIcon, LockedActivityIcon } from '../../../../shared/components';

// Skip/Forward icon (>>)
const SkipIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#6b7280" viewBox="0 0 512 512">
    <path d="M52.5 440.6c-9.5 7.9-22.8 9.7-34.1 4.4S0 428.4 0 416V96C0 83.6 7.2 72.3 18.4 67s24.5-3.6 34.1 4.4L224 214.3V96c0-12.4 7.2-23.7 18.4-29s24.5-3.6 34.1 4.4l192 160c7.3 6.1 11.5 15.1 11.5 24.6s-4.2 18.5-11.5 24.6l-192 160c-9.5 7.9-22.8 9.7-34.1 4.4s-18.4-16.6-18.4-29V297.7L52.5 440.6z"/>
  </svg>
);

/**
 * ActivitySession - Container component that handles the active activity session
 * Routes to appropriate session component based on activity type:
 * - ExternalServiceSession for duolingo, khan_academy, codecombat, brilliant, art_workout
 * - HandWritingSession for hand_writing
 * - WisdomSession for wisdom
 * - PhysicalSession for physical-skipping, physical-pickleball
 */
export const ActivitySession = ({
  session,
  activity,
  nextActivities,
  onComplete,
  onCancel,
  initialSkipRequestPending = false
}) => {
  const { t } = useTranslation();
  const [skipRequestPending, setSkipRequestPending] = useState(initialSkipRequestPending);
  const [isRequestingSkip, setIsRequestingSkip] = useState(false);
  const [showNoProgressModal, setShowNoProgressModal] = useState(false);
  const [progressComparison, setProgressComparison] = useState(null);

  // Sync skipRequestPending state with initialSkipRequestPending prop when session changes
  // This ensures the skip request pending state is tied to the current session
  useEffect(() => {
    setSkipRequestPending(initialSkipRequestPending);
  }, [session.id, initialSkipRequestPending]);

  // Handle skip request
  const handleRequestSkip = useCallback(async () => {
    setIsRequestingSkip(true);
    try {
      const result = await createSkipRequest(session.id);
      if (result.status === 'success') {
        setShowNoProgressModal(false);
        setSkipRequestPending(true);
        // Reload the page to refresh the dashboard state
        window.location.reload();
      }
    } catch (error) {
      console.error('Error requesting skip:', error);
      // Extract error message from axios error response if available
      const errorMessage = error.response?.data?.error?.message ||
                          error.response?.data?.message ||
                          t('children.activity_sessions.no_progress_dialog.error');
      alert(errorMessage);
    } finally {
      setIsRequestingSkip(false);
    }
  }, [session.id, t]);

  // Handle showing no progress modal (called from child components)
  const handleShowNoProgressModal = useCallback((comparison) => {
    setProgressComparison(comparison);
    setShowNoProgressModal(true);
  }, []);

  // Handle skip activity (used when initial loading fails)
  const handleSkipActivity = useCallback(async () => {
    try {
      const result = await skipActivity(session.id);
      if (result.error) {
        // API returned an error (e.g., session cancelled)
        if (result.error.code === 'SESSION_CANCELLED' && result.redirect_to) {
          // Session was cancelled by guardian (courses/username changed), redirect to dashboard
          window.location.href = result.redirect_to;
          return;
        }
        console.error('Error skipping activity:', result.error.message);
        alert(result.error.message);
        return;
      }
      // Reload to show the next activity
      window.location.reload();
    } catch (error) {
      console.error('Error skipping activity:', error);
      alert(t('children.activity_sessions.no_progress_dialog.error'));
    }
  }, [session.id, t]);

  // Render the appropriate session component based on activity type
  const renderSessionContent = () => {
    // Compute template key from activity_type and activity_sub_type (mirrors Ruby's template_key)
    // Examples: "physical" + "skipping" -> "physical-skipping", "duolingo" + "english" -> "duolingo-english"
    const templateKey = activity.activity_sub_type
      ? `${activity.activity_type}-${activity.activity_sub_type}`
      : activity.activity_type;

    // External services (duolingo, khan_academy, etc.) use ExternalServiceSession
    // The is_external_service flag is set by the server based on ServiceQueryStrategies::Factory
    if (activity.is_external_service) {
      return (
        <ExternalServiceSession
          session={session}
          activity={activity}
          onComplete={onComplete}
          onSkipActivity={handleSkipActivity}
          onShowNoProgressModal={handleShowNoProgressModal}
        />
      );
    }

    if (templateKey === 'hand_writing') {
      return (
        <HandWritingSession
          session={session}
          activity={activity}
          onComplete={onComplete}
        />
      );
    }

    if (templateKey === 'handcraft') {
      return (
        <HandcraftSession
          session={session}
          activity={activity}
          onComplete={onComplete}
        />
      );
    }

    if (templateKey === 'draw_with_two_hand') {
      return (
        <DrawWithTwoHandSession
          session={session}
          activity={activity}
          onComplete={onComplete}
        />
      );
    }

    if (templateKey === 'wisdom') {
      return (
        <WisdomSession
          session={session}
          activity={activity}
          onComplete={onComplete}
        />
      );
    }

    if (activity.activity_type === 'vocabulary') {
      return (
        <VocabularySession
          session={session}
          activity={activity}
          onComplete={onComplete}
        />
      );
    }

    // Physical activities use template key format (physical-skipping, physical-pickleball, physical-soccer)
    if (templateKey === 'physical-skipping' || templateKey === 'physical-pickleball' || templateKey === 'physical-soccer') {
      return (
        <PhysicalSession
          session={session}
          activity={activity}
          onComplete={onComplete}
        />
      );
    }

    // Fallback for unsupported activity types
    return (
      <div className="alert alert-warning">
        <p className="mb-0">
          Activity type "{templateKey}" is not yet supported in React.
        </p>
      </div>
    );
  };

  return (
    <>
      {/* Session Header - Activity info and Skip button */}
      <div className="d-flex justify-content-between align-items-center mb-4 pb-3" style={{ borderBottom: '1px solid #e5e7eb' }}>
        {/* Left: Activity icon + name + subtitle */}
        <div className="d-flex align-items-center gap-3">
          <ActivityIcon iconUrl={activity.icon_url} name={activity.name} size={48} />
          <div>
            <div className="fw-bold" style={{ fontSize: '16px', color: '#111826' }}>
              {activity.name}
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>
              {t('children.dashboard.current_session')}
            </div>
          </div>
        </div>

        {/* Right: Skip button */}
        {skipRequestPending ? (
          <div style={{ color: '#d97706' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 512 512">
              <path d="M256 32c14.2 0 27.3 7.5 34.5 19.8l216 368c7.3 12.4 7.3 27.7 .2 40.1S486.3 480 472 480H40c-14.3 0-27.6-7.7-34.7-20.1s-7-27.8 .2-40.1l216-368C228.7 39.5 241.8 32 256 32zm0 128c-13.3 0-24 10.7-24 24V296c0 13.3 10.7 24 24 24s24-10.7 24-24V184c0-13.3-10.7-24-24-24zm32 224a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z"/>
            </svg>
          </div>
        ) : (
          <button
            type="button"
            className="btn p-0 d-flex align-items-center gap-2"
            onClick={handleRequestSkip}
            disabled={isRequestingSkip}
            style={{ border: 'none', background: 'none' }}
          >
            {isRequestingSkip ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ color: '#6b7280' }}></span>
                <span className="fw-medium" style={{ fontSize: '16px', color: '#6b7280' }}>
                  {t('children.activity_sessions.no_progress_dialog.checking')}
                </span>
              </>
            ) : (
              <>
                <SkipIcon />
                <span className="fw-medium" style={{ fontSize: '16px', color: '#6b7280' }}>
                  {t('children.dashboard.skip_session')}
                </span>
              </>
            )}
          </button>
        )}
      </div>

      {/* Skip Request Pending Alert - shown when skip request is pending */}
      {skipRequestPending && (
        <div className="alert alert-warning d-flex align-items-center mb-4" role="alert">
          <i className="bi bi-exclamation-triangle-fill me-3" style={{ fontSize: '1.25rem' }}></i>
          <div>
            <strong>{t('children.activity_sessions.skip_request_pending.title')}</strong>
            <br />
            {t('children.activity_sessions.skip_request_pending.message')}
          </div>
        </div>
      )}

      {/* Activity content with common border */}
      <div className="card border-2 border-primary rounded-4">
        {renderSessionContent()}
      </div>

      {/* Next activities */}
      {nextActivities && nextActivities.length > 0 && (
        <div className="card border-secondary-subtle rounded-4 mt-4">
          <div className="card-body p-4">
            <h3 className="fw-semibold fs-5 mb-4 text-center text-secondary">
              {t('children.dashboard.learning_path.title')}
            </h3>
            <div className="row g-3">
              {nextActivities.map((nextActivity, index) => (
                <div key={nextActivity.id} className="col-md-4">
                  <div className="card border border-secondary-subtle rounded-3 h-100 locked-activity-card" style={{ opacity: 0.5 }}>
                    <div className="card-body p-3">
                      <div className="d-flex align-items-start gap-2">
                        <LockedActivityIcon iconUrl={nextActivity.icon_url} name={nextActivity.name} size={40} />
                        <div className="flex-grow-1">
                          <h6 className="fw-bold mb-1">{nextActivity.name}</h6>
                          <p className="text-secondary small mb-0">
                            {nextActivity.service_username || nextActivity.service_name}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* No Progress Modal - shared across all session types */}
      {showNoProgressModal && (
        <div id="progressModal" className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold">
                  {t('children.activity_sessions.no_progress_dialog.title')}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowNoProgressModal(false)}
                ></button>
              </div>
              <div className="modal-body pt-2">
                <p className="text-secondary mb-3">
                  {t('children.activity_sessions.no_progress_dialog.message')}
                </p>

                {progressComparison && progressComparison.length > 0 && (
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>{t('children.activity_sessions.no_progress_dialog.course_name')}</th>
                          <th className="text-center">{t('children.activity_sessions.no_progress_dialog.before')}</th>
                          <th className="text-center">{t('children.activity_sessions.no_progress_dialog.now')}</th>
                          <th className="text-center">{t('children.activity_sessions.no_progress_dialog.diff')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {progressComparison.map((course, index) => {
                          const diff = (course.now || 0) - (course.before || 0);
                          const diffClass = diff > 0 ? 'text-success' : diff < 0 ? 'text-danger' : 'text-secondary';
                          const diffText = diff > 0 ? `+${diff}` : diff < 0 ? `${diff}` : '0';
                          return (
                            <tr key={index}>
                              <td>{course.course_name}</td>
                              <td className="text-center">{course.before}</td>
                              <td className="text-center">{course.now}</td>
                              <td className={`text-center fw-bold ${diffClass}`} data-testid={`diff-${index}`}>
                                {diffText}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="modal-footer border-0">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowNoProgressModal(false)}
                >
                  {t('children.activity_sessions.no_progress_dialog.ok_button')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
